import {
  createContext,
  ReactNode,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { tokenStorage } from '@/config/tokenStorage';
import { TokenStorageKeys } from '@/lib/auth';
import { Logger } from '@/lib/logs/logger';

type FilterContextType = {
  organizationId: string | null;
  setOrganizationId: (id: string) => void;
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

type FilterProviderProps = {
  children: ReactNode;
  value?: FilterContextType;
};

const getStoredAuthenticatedUser = () => {
  try {
    const json = tokenStorage.getItem(TokenStorageKeys.AUTHENTICATED_USER);
    if (json) return JSON.parse(json);
  } catch (e) {
    Logger.errorOnce(`Failed to load AUTHENTICATED_USER from tokenStorage: ${e}`);
  }
  return null;
};

const StaffOrganizationIdContextProvider = ({
  organizationId: currentOrganizationId,
  setOrganizationId,
}: {
  organizationId: string | null;
  setOrganizationId: (id: string) => void;
}) => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const isStaff = getStoredAuthenticatedUser()?.user?.is_staff === true;
    if (!isStaff) return;

    const urlOrganizationId = searchParams.get('organizationId');
    if (urlOrganizationId && urlOrganizationId !== currentOrganizationId) {
      setOrganizationId(urlOrganizationId);
    }
  }, [searchParams, currentOrganizationId, setOrganizationId]);

  return null;
};

export const FilterProvider = ({ children, value }: FilterProviderProps) => {
  const [organizationId, setOrganizationIdRaw] = useState<string | null>(() => {
    const authenticatedUser = getStoredAuthenticatedUser();
    const isStaff = authenticatedUser?.user?.is_staff === true;

    if (isStaff) {
      return new URLSearchParams(window.location.search).get('organizationId') ?? null;
    }

    return authenticatedUser?.user?.membership?.organization?.id ?? null;
  });

  const setOrganizationId = useCallback((id: string) => {
    startTransition(() => setOrganizationIdRaw(id));
  }, []);

  const contextValue = useMemo(
    () => value ?? { organizationId, setOrganizationId },
    [value, organizationId, setOrganizationId],
  );

  return (
    <FilterContext.Provider value={contextValue}>
      <StaffOrganizationIdContextProvider organizationId={organizationId} setOrganizationId={setOrganizationId} />
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = useContext(FilterContext);

  if (context === undefined) {
    throw new Error('useFilterContext must be used within an FilterProvider');
  }

  return context;
};
